import React, { useEffect, useState } from "react";
import moment from "moment";
import URL from "../../../../Main/url";
import CurrencyWord from "../../../../Componets/Currencywords";
import formatDateDDMMYYYY from "../../../../Main/formatDate";
import UserCurrency from "../../../../Componets/UserCurrency";

const A4Invoice = React.forwardRef((props, ref) => {
  const { values } = props;

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  const calculateTotalPages = () => {
    if (!values?.details) return 1;
    return Math.ceil(values.details.length / itemsPerPage);
  };

  const getItemsForPage = (page) => {
    if (!values?.details) return [];
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return values.details.slice(startIndex, endIndex);
  };

  useEffect(() => {
    if (values?.details) {
      const pages = calculateTotalPages();
      setTotalPages(pages);
    }
  }, [values?.details]);

  const injectScopedStyles = () => {
    const style = document.createElement("style");
    style.innerHTML = `
      @page {
        size: A4;
        margin: 3% 6% 5% 6%;

         @bottom-left {
          content: "Printed on: ${formatDateDDMMYYYY(new Date().toISOString())}";
          font-size: 10px;
        }
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font-size: 10px;
        }
      }  
      
      @media print {
        .header-section {
          position: fixed;
          top: 0%;
          left: 0%;
          right: 0%;
          background: white;
          z-index: 1000;
          page-break-inside: avoid;
        }
        
        .table-header-section {
          position: fixed;
          top: 28%; 
          left: 0%;
          right: 0%;
          background: white;
          z-index: 999;
          page-break-inside: avoid;
        }
        
        .invoice-content {
          margin-top: 47.5%; 
        }
        
        .page-break {
          page-break-before: always;
          margin-top: 47.5%; 
        }
        
        .a4-invoice-footer {
          font-size: 12px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          left: 6%;
        }
  
        .a4-invoice-footer-right {
          font-size: 12px;
          white-space: pre;
          position: fixed;
          bottom: 10mm;
          right: 6%;
        }
      }

      .invoice-table {
        table-layout: fixed;
        width: 100%;
      }

      .col-sno { width: 5%; }
      .col-desc { width: 17%; }
      .col-frame { width: 6%; }
      .col-lens { width: 6%; }
      .col-batch { width: 10%; }
      .col-price { width: 7%; }
      .col-qty { width: 5%; }
      .col-foc { width: 5%; }
      .col-ws { width: 8%; }
      .col-disc { width: 8%; }
      .col-amount { width: 9%; }
      .col-vat { width: 7%; }
      .col-vatamt { width: 8%; }
      .col-total { width: 5%; }
    `;
    document.head.appendChild(style);
  };

  useEffect(() => {
    if (values?.branch?.name) {
      injectScopedStyles();
    }
  }, [values?.branch?.name]);

  const renderHeaderSection = () => (
    <div className="header-section">
      <div className="flex justify-between items-start">
        <div className="text-black-900 text-[0.65rem]">
          <div className="text-bold text-md">{capitalizeWords(values?.branch?.name)}</div>
          <div className="text-medium text-xs">
            Email&nbsp;:&nbsp;
            {values?.branch?.email ? values?.branch?.email + ", " : ""}
            &nbsp;
          </div>
          <div className="text-medium text-xs">
            Phone&nbsp;:&nbsp;
            {values?.branch?.phone ? values?.branch?.phone : ""}
            &nbsp;
          </div>
          <div className="text-xs">
            {values?.branch?.area ? values?.branch?.area + ", " : ""}
            {values?.branch?.city ? values?.branch?.city + ", " : ""}
            {values?.branch?.country ? values?.branch?.country : ""}
          </div>
          <div className="text-xs">
            VATIN&nbsp;:&nbsp;
            {values?.branch?.tax ? values?.branch?.tax : "N/A"}
          </div>
        </div>
        {values?.branch?.image ? (
          <div className="flex justify-center flex-col items-center">
            <div className="text-xl pb-2">
              <img
                className="w-[50px] h-[50px]"
                src={`${URL}/uploads${values?.branch?.image}`}
                alt="Branch Logo"
              />
            </div>
            <div className="text-bold text-lg">{capitalizeWords(values?.branch?.name)}</div>
          </div>
        ) : (
          <div className="flex justify-center flex-col items-center">
            <div
              className="w-[50px] h-[50px] rounded-md bg-gray-300 text-black flex items-center justify-center text-xl font-semibold"
            >
              {values?.branch?.name
                ? values.branch.name.charAt(0).toUpperCase()
                : "N"}
            </div>
            <div className="text-bold text-lg">{capitalizeWords(values?.branch?.name)}</div>
          </div>
        )}
      </div>

      <hr className="my-2 border-t-black-900 border-black" />
      <div className="text-medium text-md text-center py-1">TAX INVOICE</div>
      <hr className="my-2 border-t-black-900 border-black" />

      <div className="border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
        <div className="flex justify-between">
          <div>
            <div className="font-semibold text-[0.70rem] mb-2">Customer Name</div>
            <div className="text-[0.65rem] space-y-1">
              <div className="font-medium">
                {values?.customer?.name
                  ? values?.customer?.name
                  : "Walk-in-customer"}
              </div>

              {values?.customer?.phone && (
                <div>
                  <span className="font-semibold">Phone :</span>{" "}
                  {values?.customer?.phone}
                </div>
              )}

              {values?.customer?.email && (
                <div>
                  <span className="font-semibold">Email :</span>{" "}
                  {values?.customer?.email}
                </div>
              )}

              <div>
                {values?.customer?.city ? values?.customer?.city + ", " : ""}
                {values?.customer?.state}
              </div>

              <div>{values?.customer?.country}</div>

              {values?.customer?.tax && (
                <div>
                  <span className="font-semibold">VATIN :</span>{" "}
                  {values?.customer?.tax}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-[0.65rem] space-y-1">
              <div className="flex">
                <div className="w-24 text-bold">Invoice No</div>
                <div className="px-2">:</div>
                <div className="text-right">#INV-{values?.number}</div>
              </div>
              <div className="flex">
                <div className="w-24 text-bold">Invoice Type</div>
                <div className="px-2">:</div>
                <div className="text-right">
                  {values?.payment_status == 2
                    ? values?.payments
                      ?.map((v, i) => (i > 0 ? ", " : "") + v?.name)
                      .join("")
                    : "CREDIT"}
                </div>
              </div>
              <div className="flex">
                <div className="w-24 text-bold">Date</div>
                <div className="px-2">:</div>
                <div className="text-right">
                  {formatDateDDMMYYYY(values?.date)}
                </div>
              </div>
              {values?.due_date ? (
                <div className="flex">
                  <div className="w-24">Due Date</div>
                  <div className="px-2">:</div>
                  <div className="text-right">
                    {formatDateDDMMYYYY(values?.due_date)}
                  </div>
                </div>
              ) : (
                ""
              )}
              {values?.lpo_number ? (
                <div className="flex">
                  <div className="w-24">Lpo No</div>
                  <div className="px-2">:</div>
                  <div className="text-right">{values?.lpo_number}</div>
                </div>
              ) : (
                ""
              )}
              {values?.lpo_date ? (
                <div className="flex">
                  <div className="w-24">Lpo Date</div>
                  <div className="px-2">:</div>
                  <div className="text-right">
                    {formatDateDDMMYYYY(values?.lpo_date)}
                  </div>
                </div>
              ) : (
                ""
              )}
              {values?.salesman?.name ? (
                <div className="flex">
                  <div className="w-24">Salesman</div>
                  <div className="px-2">:</div>
                  <div className="text-right">{values?.salesman?.name}</div>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTableHeaders = () => (
    <div className="table-header-section">
      <table className="invoice-table w-full border-separate border-spacing-0 rounded-t-lg overflow-hidden border border-gray-300 mb-0">
        <thead>
          <tr className="bg-gray-100">
            <th className="col-sno text-[0.60rem] text-left py-2 pl-3 border-b border-gray-300 rounded-tl-lg">
              S.NO
            </th>
            <th className="col-desc text-[0.60rem] text-left p-1 border-b border-gray-300">
              DESCRIPTION
            </th>
            <th className="col-frame text-[0.60rem] text-center p-1 border-b border-gray-300">
              FRAME
            </th>
            <th className="col-lens text-[0.60rem] text-center p-1 border-b border-gray-300">
              LENS
            </th>
            <th className="col-batch text-[0.60rem] text-left p-1 border-b border-gray-300">
              BATCH / EXPIRY
            </th>
            <th className="col-price text-[0.60rem] text-left p-1 border-b border-gray-300">
              RET.PRICE
            </th>
            <th className="col-qty text-[0.60rem] text-left p-1 border-b border-gray-300">
              QTY
            </th>
            <th className="col-foc text-[0.60rem] text-left p-1 border-b border-gray-300">
              FOC
            </th>
            <th className="col-ws text-[0.60rem] text-left p-1 border-b border-gray-300">
              W.S PRICE
            </th>
            <th className="col-disc text-[0.60rem] text-left p-1 border-b border-gray-300">
              DISC
            </th>
            <th className="col-amount text-[0.60rem] text-left p-1 border-b border-gray-300">
              <div className="text-bold">AMOUNT</div>
              <div className="text-[0.50rem] lowercase">(Before VAT)</div>
            </th>
            <th className="col-vat text-[0.60rem] text-left p-1 border-b border-gray-300">
              VAT%
            </th>
            <th className="col-vatamt text-[0.60rem] text-left p-1 border-b border-gray-300">
              VAT Amount
            </th>
            <th className="col-total text-[0.60rem] text-left pr-2 border-b border-gray-300 rounded-tr-lg">
              <div className="text-bold">TOTAL</div>
              <div className="text-[0.50rem] lowercase">(With VAT)</div>
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );

  const renderTableBody = (pageItems, pageNumber, isLastPage) => (
    <div key={`page-${pageNumber}`} className={pageNumber > 1 ? "page-break" : "invoice-content"}>
      <table className="invoice-table w-full border-separate border-spacing-0 overflow-hidden border border-gray-300 border-t-0">
        <tbody className="text-sm">
          {pageItems.map((value, index) => {
            const isLastDataRow = index === pageItems.length - 1;
            const globalIndex = (pageNumber - 1) * itemsPerPage + index;
            const isLastOverallRow = isLastPage && isLastDataRow;

            return (
              <tr key={`${pageNumber}-${index}`} className="hover:bg-gray-50">
                <td className={`col-sno text-[0.60rem] text-left p-1 pl-3 ${isLastOverallRow ? "rounded-bl-lg" : ""}`}>
                  {globalIndex + 1}
                </td>
                <td className="col-desc text-[0.60rem] ">
                  {value?.name}
                </td>
                <td className="col-frame text-center text-[0.60rem] p-1">
                  {value?.frame_type || ""}
                </td>
                <td className="col-lens text-center text-[0.60rem] p-1">
                  {value?.lens_type || ""}
                </td>
                <td className="col-batch text-[0.60rem] text-left p-1">
                  <div className="text-[0.60rem]  ">
                    {value?.description?.number}
                  </div>
                  <div className="text-[0.60rem] text-left">
                    {value?.expiry_date
                      ? `${formatDateDDMMYYYY(value?.expiry_date)}`
                      : ""}
                  </div>
                </td>
                <td className="col-price text-left text-[0.60rem] p-1">
                  {value?.description?.sale_price?.toFixed(3)}
                </td>
                <td className="col-qty text-left text-[0.60rem] p-1">
                  {value?.quantity}
                </td>
                <td className="col-foc text-left text-[0.60rem] p-1">
                  {value?.free || 0}
                </td>
                <td className="col-ws  text-left text-[0.60rem] p-1">
                  {value?.sale_price?.toFixed(3)}
                </td>
                <td className="col-disc text-left text-[0.60rem] p-1">
                  <div className="leading-[1rem]">{value?.discount_percentage}%</div>
                  <div className="text-gray-500 leading-[1rem]">
                    {value?.discount_price?.toFixed?.(3)}
                  </div>
                </td>
                <td className="col-amount text-left text-[0.60rem] p-1 pr-2">
                  {(value?.quantity * value?.sale_price).toFixed(3)}
                </td>
                <td className="col-vat text-left text-[0.60rem] p-1">
                  <div>{value?.tax}%</div>
                </td>
                <td className="col-vatamt text-left text-[0.60rem] p-1">
                  <div className="text-gray-500">
                    {value?.tax_amount?.toFixed(3)}
                  </div>
                </td>
                <td className={`col-total text-left text-[0.60rem] p-1 pr-2 ${isLastOverallRow ? "rounded-br-lg" : ""}`}>
                  {value?.total?.toFixed(3)}
                </td>
              </tr>
            );
          })}

          {pageItems.length < itemsPerPage && Array.from({
            length: itemsPerPage - pageItems.length,
          }).map((_, index, arr) => {
            const isLastEmptyRow = index === arr.length - 1;
            const lastRowRounded = isLastPage && isLastEmptyRow;

            return (
              <tr key={`empty-${pageNumber}-${index}`} className="hover:bg-gray-50">
                <td className={`text-[0.60rem] p-2 pl-3 ${lastRowRounded ? "rounded-bl-lg" : ""}`}>
                  &nbsp;
                </td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className="p-2">&nbsp;</td>
                <td className={`text-right text-[0.60rem] p-1 pr-2 ${lastRowRounded ? "rounded-br-lg" : ""}`}>
                  &nbsp;
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isLastPage && (
        <div className="border border-gray-300 rounded-lg p-4 mb-2 mt-2 shadow-sm">
          <div className="flex justify-between">
            <div className="text-[0.65rem] w-1/2">
              <div className="font-semibold mb-2 text-[0.70rem]">
                Total in words&nbsp;:&nbsp;
                <span className="font-normal">
                  <CurrencyWord amount={values?.total?.toFixed(3)} />
                </span>
              </div>
            </div>

            <div className="text-[0.65rem] w-1/2">
              <table className="w-full text-[0.65rem]">
                <tbody>
                  <tr>
                    <td className="text-right pr-2 py-1">Subtotal</td>
                    <td className="text-right w-20 py-1">{values?.subtotal?.toFixed(3)}</td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">VAT (5%)</td>
                    <td className="text-right py-1">{values?.taxamount?.toFixed(3)}</td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">Discount</td>
                    <td className="text-right py-1">
                      {values?.discount_type
                        ? values?.discount_value
                          ? `${values?.discount?.toFixed(3)} (-${values?.discount_value}%)`
                          : "0.000"
                        : values?.discount
                          ? `-${values?.discount?.toFixed(3)}`
                          : "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-1">Delivery Charges</td>
                    <td className="text-right py-1">
                      {values?.delivery?.toFixed(3) || "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-2 py-2 font-semibold">Total (<UserCurrency />)</td>
                    <td className="text-right py-2 font-semibold">{values?.total?.toFixed(3)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {isLastPage && (
        <div className="flex justify-between pt-10 text-[0.60rem]">
          <div className="text-center">
            <div className="border-b border-gray-400 w-32 mb-1"></div>
            <div className="text-bold">Received By</div>
          </div>
          <div className="text-center">
            <div className="border-b border-gray-400 w-32 mb-1"></div>
            <div className="text-bold">Checked By</div>
          </div>
          <div className="text-center">
            <div className="border-b border-gray-400 w-32 mb-1"></div>
            <div className="text-bold">Prepared By</div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="a4-invoice" ref={ref}>
      <style>
        {`
          @media print {
            .a4-invoice {
              page-break-before: always;
            }
            
            @page {
              size: A4;
              margin: 3% 6% 5% 6%;
            }
            
            .a4-invoice {
              page-break-inside: avoid;
            }
          }
        `}
      </style>

      {renderHeaderSection()}

      {renderTableHeaders()}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
        const pageItems = getItemsForPage(pageNumber);
        const isLastPage = pageNumber === totalPages;
        return renderTableBody(pageItems, pageNumber, isLastPage);
      })}
    </div>
  );
});

export default A4Invoice;